import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Plus, Edit, Trash2, User, Star, Gift } from 'lucide-react';
import { useBiographyLibrary } from '@/hooks/useBiographyLibrary';
import { BiographyEditorModal } from '@/components/BiographyEditorModal';
import type { UserBiography } from '@/hooks/useBiographyLibrary';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const BiographyLibraryPage = () => {
  const navigate = useNavigate();
  const {
    biographies,
    canAddMore,
    isLoading,
    deleteBiography,
    createBiography,
    updateBiography,
    setDefaultBiography,
  } = useBiographyLibrary();
  const [editingBiography, setEditingBiography] = useState<UserBiography | null>(
    null
  );
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    // Backend handles auto-promotion of default if needed
    // Only restriction: cannot delete if it's the user's only biography
    if (biographies.length <= 1) {
      toast.error('Cannot delete your only biography. Create another first.');
      return;
    }
    setDeleteConfirmId(id);
  };

  const confirmDelete = () => {
    if (deleteConfirmId) {
      deleteBiography.mutate(deleteConfirmId);
      setDeleteConfirmId(null);
    }
  };

  const handleSetAsDefault = (biography: UserBiography) => {
    setDefaultBiography.mutate(biography.id);
  };

  const handleSendGift = (biography: UserBiography) => {
    navigate(`/send-gift?biographyId=${biography.id}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading biographies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Users className="h-8 w-8 text-primary" />
          <h1 className="text-4xl font-bold">Biography Library</h1>
        </div>
        <p className="text-muted-foreground text-lg">
          Manage biographies for yourself and loved ones. Create personalized
          sessions for anyone in your library.
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          {biographies.length} of 10 biographies used
        </p>
        {!canAddMore && (
          <p className="text-sm text-destructive mt-2">
            You've reached the maximum of 10 biographies. Delete one to add
            another.
          </p>
        )}
      </div>

      {/* Add New Biography Button */}
      <div className="mb-6">
        <Button
          onClick={() => setIsCreating(true)}
          disabled={!canAddMore}
          size="lg"
          className="w-full sm:w-auto"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add New Biography
          {!canAddMore && ' (Limit Reached)'}
        </Button>
      </div>

      {/* Biographies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {biographies.map((biography) => (
          <Card
            key={biography.id}
            className={biography.is_default ? 'border-primary border-2' : ''}
          >
            <CardContent className="p-6">
              {/* Badge for default biography */}
              {biography.is_default && (
                <div className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium mb-3">
                  <User className="h-3 w-3" />
                  You
                </div>
              )}

              {/* Biography Name */}
              <h3 className="text-xl font-bold mb-2">{biography.biography_name}</h3>

              {/* Name Details */}
              <div className="text-sm text-muted-foreground mb-3">
                {biography.first_name && <p>Name: {biography.first_name}</p>}
                {biography.preferred_name && (
                  <p>Preferred: {biography.preferred_name}</p>
                )}
              </div>

              {/* Biography Preview */}
              <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                {biography.biography_text.substring(0, 150)}
                {biography.biography_text.length > 150 && '...'}
              </p>

              {/* Character Count */}
              <p className="text-xs text-muted-foreground mb-4">
                {biography.biography_text.length} / 3000 characters
              </p>

              {/* Actions */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingBiography(biography)}
                  className="flex-1"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                {!biography.is_default && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSendGift(biography)}
                    title="Send as gift"
                  >
                    <Gift className="h-4 w-4" />
                  </Button>
                )}
                {!biography.is_default && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSetAsDefault(biography)}
                    title="Set as default"
                  >
                    <Star className="h-4 w-4" />
                  </Button>
                )}
                {/* Show delete for any biography if user has more than one */}
                {biographies.length > 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(biography.id)}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Empty State */}
        {biographies.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No biographies yet</h3>
            <p className="text-muted-foreground mb-4">
              Add your first biography to start creating personalized sessions
            </p>
            <Button onClick={() => setIsCreating(true)}>
              <Plus className="h-5 w-5 mr-2" />
              Add Biography
            </Button>
          </div>
        )}
      </div>

      {/* Biography Editor Modal */}
      <BiographyEditorModal
        biography={editingBiography}
        isOpen={isCreating || !!editingBiography}
        onClose={() => {
          setIsCreating(false);
          setEditingBiography(null);
        }}
        createBiography={createBiography}
        updateBiography={updateBiography}
        isLoading={isLoading}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteConfirmId}
        onOpenChange={() => setDeleteConfirmId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Biography?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this
              biography from your library.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BiographyLibraryPage;
